import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { logCategoryAsString, logLevelAsString } from '@leedpulse-monorepo/enums';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getLogs from '../../../domain/use_cases/logs/getLogs';
import moment from 'moment';
import { ISubscriptionLog } from '@leedpulse-monorepo/interfaces';

interface SubscriptionLogsProps {
  subscriptionId: string;
}

const SubscriptionLogs: React.FC<SubscriptionLogsProps> = ({ subscriptionId }) => {
  const [logs, setLogs] = useState<ISubscriptionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData, DocumentData> | undefined>(undefined);
  const [selectedLog, setSelectedLog] = useState<string | null>(null);

  const fetchLogs = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const result = await getLogs(
        append ? lastVisible : undefined,
        { subscriptionId }
      );
      setLogs(prev => append ? [...prev, ...(result.items as ISubscriptionLog[])] : (result.items as ISubscriptionLog[]));
      setLastVisible(result.lastVisible);
    } catch (e) {
      console.error('[SubscriptionLogs] Failed to fetch logs:', e);
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, lastVisible]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    fetchLogs();
  }, [subscriptionId]);

  return (
    <div className="list-container">
      <div className="list-container__controls">
        <h2>Logs IA</h2>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => fetchLogs(false)}
        >
          Rafraichir
        </Button>
      </div>
      <Table
        loading={loading}
        pagination={false}
        rowKey="id"
        columns={[
          {
            align: 'center',
            title: 'Type',
            dataIndex: 'level',
            key: 'level',
            width: 80,
            render: (value) => logLevelAsString(value),
          },
          {
            title: 'Catégorie',
            dataIndex: 'category',
            key: 'category',
            render: (value) => logCategoryAsString(value),
          },
          {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (value) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            render: (value) => (
              <div
                style={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 600,
                }}
                onClick={() => setSelectedLog(value)}
              >
                {value}
              </div>
            ),
          },
        ]}
        dataSource={logs}
      />
      <Modal
        title="Detail du log"
        open={!!selectedLog}
        onCancel={() => setSelectedLog(null)}
        footer={null}
        width={700}
      >
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '60vh',
            overflow: 'auto',
            fontSize: 13,
          }}
        >
          {selectedLog}
        </pre>
      </Modal>
      {logs.length > 0 && logs.length % 100 === 0 && (
        <Button
          onClick={() => fetchLogs(true)}
          loading={loading}
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: '1rem',
            display: 'block',
          }}
        >
          Charger plus...
        </Button>
      )}
    </div>
  );
};

export default SubscriptionLogs;
